import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import * as styles from "../styles"
import { previewAgentUrl } from "utils/visualAgentUrls"
import { buildVisualSrcdoc } from "utils/visualSrcdoc"

/**
 * Read-only counterpart to the composer's EditableVisual: renders a Willow Create visual's
 * self-contained HTML in a sandboxed iframe so the Create tab reflects the latest edit
 * immediately (the HTML is saved synchronously, unlike the async PNG/PDF re-render).
 *
 * The iframe is sandboxed with allow-scripts but NOT allow-same-origin, so the client/LLM-authored
 * HTML runs at an opaque origin and cannot reach the parent DOM. The only script loaded into it handles
 * carousel layout + paging (driven by the side arrows over postMessage) — there is no editing here.
 */

const SLIDE_WIDTH = 1080
const SLIDE_HEIGHT = 1350
const ASPECT = SLIDE_WIDTH / SLIDE_HEIGHT // 0.8 (4:5 portrait)

interface StaticVisualProps {
  html: string
}

const StaticVisual: React.FC<StaticVisualProps> = ({ html }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [width, setWidth] = useState(0)
  const [slides, setSlides] = useState(1)
  const [current, setCurrent] = useState(0)

  const srcdoc = useMemo(() => buildVisualSrcdoc(html, previewAgentUrl), [html])

  // Reset paging whenever the visual changes (e.g. after an edit re-fetch swaps in new HTML).
  useEffect(() => {
    setCurrent(0)
  }, [html])

  // Measure the container so the fixed 1080-wide canvas can be scaled to fill the card width.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const update = () => setWidth(container.clientWidth)
    update()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(update)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Only trust messages from our own iframe.
      if (event.source !== iframeRef.current?.contentWindow) return
      const data = event.data || {}
      if (data.source !== "static-visual") return
      if (data.type === "ready") setSlides(data.slides)
      else if (data.type === "slide") setCurrent(data.index)
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [])

  // targetOrigin "*" is safe: the iframe is sandboxed without allow-same-origin (opaque origin
  // that cannot be named), and inbound messages are filtered by event.source + data.source above.
  const toAgent = useCallback((message: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage({ target: "static-visual", ...message }, "*")
  }, [])

  const onIframeLoad = useCallback(() => toAgent({ type: "init" }), [toAgent])

  const scale = width / SLIDE_WIDTH
  const height = width / ASPECT
  const isCarousel = slides > 1

  return (
    <div ref={containerRef} data-testid="static-visual" style={{ width: "100%" }}>
      <div style={{ position: "relative", width: "100%", height, overflow: "hidden", background: "#fff" }}>
        {width > 0 && (
          <iframe
            ref={iframeRef}
            title="visual-preview"
            sandbox="allow-scripts"
            srcDoc={srcdoc}
            onLoad={onIframeLoad}
            // pointer-events none: a read-only thumbnail driven only by the side arrows, so it
            // never hijacks page scroll or selection.
            style={{ width: SLIDE_WIDTH, height: SLIDE_HEIGHT, border: 0, transform: `scale(${scale})`, transformOrigin: "top left", pointerEvents: "none" }}
          />
        )}
        {isCarousel && current > 0 && (
          <styles.SlideArrow $side="left" type="button" aria-label="Previous slide" onClick={() => toAgent({ type: "go", index: current - 1 })}>
            ‹
          </styles.SlideArrow>
        )}
        {isCarousel && current < slides - 1 && (
          <styles.SlideArrow $side="right" type="button" aria-label="Next slide" onClick={() => toAgent({ type: "go", index: current + 1 })}>
            ›
          </styles.SlideArrow>
        )}
      </div>
    </div>
  )
}

const sideArrow: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  width: 32,
  height: 32,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "none",
  background: "rgba(15, 23, 42, 0.45)",
  color: "#fff",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer",
}

export default StaticVisual
